import { call } from 'redux-saga/effects';
import { buildAwardCategoryCreatePayload } from '../../pages/BrandDashboard/contestWizard/buildAwardCategoryPayload';
import { awardsCategoryUrl, getContestUrl } from '../../configurations/api/url';
import { api } from '../../configurations/api';
import { httpRequest } from '../types';

function stripCategoryShell(cat: Record<string, unknown>) {
    const tiered = cat.with_tiered_pricing !== false;
    return {
        ...cat,
        category_awards: [],
        with_tiered_pricing: tiered,
        with_fee: cat.with_fee !== false,
        prize_structure: (Array.isArray(cat.prize_structure) ? cat.prize_structure : []).map(
            (p: { position?: string }) => ({
                position: p.position,
                payouts: [],
            }),
        ),
    };
}

function normalizePrizeStructureForPatch(prize_structure: unknown): Array<{ position: string; payouts: string[] }> {
    if (!Array.isArray(prize_structure)) return [];
    return prize_structure
        .map(pl => {
            const row = pl as { position?: string; payouts?: unknown[] };
            return {
                position: String(row.position || ''),
                payouts: (row.payouts || [])
                    .map(pay => {
                        if (typeof pay === 'string' && pay) return pay;
                        if (pay && typeof pay === 'object' && '_id' in pay) {
                            return String((pay as { _id: string })._id);
                        }
                        return '';
                    })
                    .filter(Boolean),
            };
        })
        .filter(pl => pl.position);
}

/** Existing server categories: preserve tiers and award ids; new ones get empty prize shells. */
function categoryToPatchPayload(cat: Record<string, unknown>): Record<string, unknown> {
    if (cat._id) {
        return {
            _id: cat._id,
            category_name: cat.category_name,
            category_description: cat.category_description,
            with_fee: cat.with_fee,
            with_tiered_pricing: cat.with_tiered_pricing !== false,
            currency: cat.currency,
            fee: cat.fee,
            tier_fee: cat.tier_fee,
            prize_structure: normalizePrizeStructureForPatch(cat.prize_structure),
        };
    }
    return stripCategoryShell(cat);
}

/** New payout rows from the wizard (no existing AwardCategory id). */
function isWizardDraftPayout(pay: unknown): boolean {
    if (typeof pay === 'string') return false;
    if (!pay || typeof pay !== 'object' || Array.isArray(pay)) return false;
    const row = pay as Record<string, unknown>;
    if (row._id) return false;
    return 'label' in row || Array.isArray(row.additional_prize);
}

/** Wizard row tied to an existing AwardCategory (has _id, not a full GET document). */
function isWizardExistingPayout(pay: unknown): boolean {
    if (!pay || typeof pay !== 'object' || Array.isArray(pay)) return false;
    const row = pay as Record<string, unknown>;
    if (!row._id) return false;
    if (row.contest != null || row.created_by != null || row.createdAt != null) return false;
    return 'label' in row || typeof row.prize === 'number' || typeof row.prize === 'string';
}

/** Existing award edited in wizard, or a new draft payout that must be provisioned. */
function payoutNeedsAwardWork(pay: unknown): boolean {
    if (isWizardDraftPayout(pay)) return true;
    return isWizardExistingPayout(pay);
}

function prizeStructureNeedsAwardProvisioning(prize_structure: unknown): boolean {
    if (!Array.isArray(prize_structure)) return false;
    return prize_structure.some(pl => {
        const payouts = (pl as { payouts?: unknown[] }).payouts || [];
        return payouts.some(payoutNeedsAwardWork);
    });
}

function tierFeeIdByLabel(tierFee: Array<{ label?: string; _id?: string }> | undefined): Record<string, string> {
    const map: Record<string, string> = {};
    (tierFee || []).forEach(t => {
        if (t?.label && t._id) map[t.label] = String(t._id);
    });
    return map;
}

function* postAwardCategory(
    payload: ReturnType<typeof buildAwardCategoryCreatePayload>,
): Generator<unknown, { _id?: string }> {
    const response = yield call(api, awardsCategoryUrl, httpRequest.POST, payload, 0, 0);
    const data =
        (response as { data?: { data?: { _id?: string } } }).data?.data ??
        (response as { data?: { _id?: string } }).data;
    if (!data?._id) {
        throw new Error('Award category API did not return an id');
    }
    return data;
}

function* patchAwardCategory(
    awardId: string,
    payload: ReturnType<typeof buildAwardCategoryCreatePayload>,
): Generator<unknown, { _id: string }> {
    yield call(api, `${awardsCategoryUrl}/${awardId}`, httpRequest.PATCH, payload, 0, 0);
    return { _id: awardId };
}

/**
 * After contest POST: save submission categories, then POST /awardcategory per payout,
 * then PATCH contest prize_structure — same sources funding-requirements uses.
 */
export function* provisionContestCategoriesAndAwards(
    contestId: string,
    categories: Record<string, unknown>[],
): Generator<unknown, Record<string, unknown>> {
    const shells = categories.map(categoryToPatchPayload);

    const patch1Res = yield call(
        api,
        `${getContestUrl}/${contestId}`,
        httpRequest.PATCH,
        { categories: shells },
        0,
        0,
        false,
    );
    const patch1Data =
        (patch1Res as { data?: { data?: Record<string, unknown> } }).data?.data ??
        (patch1Res as { data?: Record<string, unknown> }).data;
    const savedCategories = (patch1Data?.categories as Record<string, unknown>[]) ?? shells;

    const processedCategories: Record<string, unknown>[] = [];

    for (let i = 0; i < categories.length; i++) {
        const sourceCat = categories[i];
        const savedCat = savedCategories[i] ?? sourceCat;
        const isExisting = Boolean(sourceCat._id);
        const needsAwards = prizeStructureNeedsAwardProvisioning(sourceCat.prize_structure);

        if (isExisting && !needsAwards) {
            processedCategories.push({
                ...savedCat,
                with_tiered_pricing: true,
                prize_structure: normalizePrizeStructureForPatch(savedCat.prize_structure ?? sourceCat.prize_structure),
            });
            continue;
        }

        const tierIds = tierFeeIdByLabel(savedCat.tier_fee as Array<{ label?: string; _id?: string }> | undefined);
        const processedPrizeStructure: Array<{ position: string; payouts: string[] }> = [];

        for (const placement of (sourceCat.prize_structure as Array<{
            position?: string;
            payouts?: Array<Record<string, unknown> | string>;
        }>) || []) {
            const payoutIds: string[] = [];

            for (const payout of placement.payouts || []) {
                if (typeof payout === 'string' && payout) {
                    payoutIds.push(payout);
                    continue;
                }
                if (!payout || typeof payout !== 'object') continue;

                const payoutRow = payout as Record<string, unknown>;
                if (payoutRow._id && (payoutRow.contest != null || payoutRow.created_by != null)) {
                    payoutIds.push(String(payoutRow._id));
                    continue;
                }

                const existingAwardId = '_id' in payout && payout._id ? String((payout as { _id: string })._id) : '';

                const extras = Array.isArray(payout.additional_prize)
                    ? (payout.additional_prize as string[]).filter(x => x?.trim())
                    : [];
                const prizeDollars = Number(payout.prize) || 0;

                if (prizeDollars <= 0 && extras.length === 0) continue;

                if (!savedCat._id) {
                    throw new Error('Submission category must be saved before creating award categories');
                }

                const awardPayload = buildAwardCategoryCreatePayload({
                    contestId,
                    submissionCategoryId: String(savedCat._id),
                    placementPosition: String(placement.position || 'Prize'),
                    tierLabel: String(payout.label || 'Standard'),
                    tierName: tierIds[String(payout.label)] || String(payout.label),
                    prizeDollars,
                    additionalPrizes: extras,
                });

                if (existingAwardId) {
                    yield call(patchAwardCategory, existingAwardId, awardPayload);
                    payoutIds.push(existingAwardId);
                } else {
                    const award = (yield call(postAwardCategory, awardPayload)) as { _id: string };
                    payoutIds.push(award._id);
                }
            }

            if (placement.position) {
                processedPrizeStructure.push({
                    position: placement.position,
                    payouts: payoutIds,
                });
            }
        }

        processedCategories.push({
            ...savedCat,
            with_tiered_pricing: true,
            prize_structure: processedPrizeStructure,
        });
    }

    const patch2Res = yield call(
        api,
        `${getContestUrl}/${contestId}`,
        httpRequest.PATCH,
        { categories: processedCategories },
        0,
        0,
        false,
    );

    return (
        (patch2Res as { data?: { data?: Record<string, unknown> } }).data?.data ??
        (patch2Res as { data?: Record<string, unknown> }).data ??
        patch1Data ?? { _id: contestId }
    );
}
